/**
 * 路線價格缺口偵測（2026-10-10）
 *
 * 要解決的問題：平台的報價是查表查出來的，表上沒有的路線，旅行社在建單最後一步
 * 會看到「此路線尚未開放線上計價」而送不出單。系統不會記錄這次失敗，也不會通知
 * 平台，所以漏單是沉默發生的 —— 盤點生產資料時就發現香港唯一啟用的車款少了三條
 * 點對點價格，香港的市區點對點等於接不到單。
 *
 * 判準（為什麼是這一條，而不是「把所有可能的組合都列出來」）：
 *   以「這個 region 曾經建過價的路線」當作業務上要做的路線，再檢查每個啟用中的
 *   車款有沒有可用的價格。報出來的是「這條路線設過價，但現在沒人能接」。
 *
 *   若改成列舉所有可能組合（機場 × zone × 方向 + zone × zone），台灣會冒出
 *   「新北↔桃園」這種從來沒建過的組合 —— 那比較像業務上本來就不提供，不是漏補。
 *   系統不去猜業務意圖，只看這條路線有沒有被建過。
 *
 * 「曾經建過」刻意包含已停用的價格列，這是踩過的坑：生產上香港 42 筆價格列只有
 * 6 筆是啟用的，因為舊車款退役時價格列跟著一起停用了。若只看啟用中的列來推算
 * 「有在賣的路線」，那三條點對點會整條消失，現役車款缺它們也偵測不到 —— 而那
 * 正是最該提醒的情況。所以「這條路線在不在營業範圍」看全部列，「這個車款有沒有
 * 價」只認啟用中的列。
 *
 * 停用的車款不列入檢查：車款停用代表這台車不賣了，不需要為它補價。
 */
import { REGIONS, type Region } from "./const.js";

type EndpointType = "airport" | "zone";

export type GapRouteRow = {
  region: Region;
  originType: EndpointType;
  originCode: string;
  destType: EndpointType;
  destCode: string;
  vehicleModelId: number;
  isActive: "yes" | "no";
};

export type GapVehicleModel = {
  id: number;
  name: string;
  /** null = 兩區共用，兩個 region 的路線都要有價 */
  region: Region | null;
  isActive: "yes" | "no";
};

export type RoutePriceGap = {
  region: Region;
  originType: EndpointType;
  originCode: string;
  destType: EndpointType;
  destCode: string;
  vehicleModelId: number;
  modelName: string;
};

/**
 * 路線的比對鍵。zone 對 zone 的點對點計價不分方向（booking-pricing 找不到 a→b
 * 會再找 b→a），所以兩個 code 排序後視為同一條；機場接送的接機與送機是不同價格，
 * 保持方向。
 */
const routeKey = (r: {
  region: Region;
  originType: EndpointType;
  originCode: string;
  destType: EndpointType;
  destCode: string;
}): string => {
  if (r.originType === "zone" && r.destType === "zone") {
    const [a, b] = [r.originCode, r.destCode].sort();
    return `${r.region}|zone:${a}|zone:${b}`;
  }
  return `${r.region}|${r.originType}:${r.originCode}|${r.destType}:${r.destCode}`;
};

const regionsFor = (m: GapVehicleModel): readonly Region[] => (m.region === null ? REGIONS : [m.region]);

/**
 * 回傳「啟用中的車款少了哪幾條該 region 已經在賣的路線」。
 * 排序：region → 車款 → 路線，讓畫面上同一個車款的缺口排在一起。
 */
export const findRoutePriceGaps = (
  rows: readonly GapRouteRow[],
  models: readonly GapVehicleModel[],
): RoutePriceGap[] => {
  const activeRows = rows.filter((r) => r.isActive === "yes");
  const activeModels = models.filter((m) => m.isActive === "yes");

  // 這個 region 的營業路線：看全部價格列（含已停用），理由見檔頭說明。
  // 取一列當代表，用來顯示起迄點。
  const serviced = new Map<string, GapRouteRow>();
  for (const r of rows) {
    const k = routeKey(r);
    if (!serviced.has(k)) serviced.set(k, r);
  }

  // 「這個車款有這條路線的價」的索引
  const covered = new Set(activeRows.map((r) => `${routeKey(r)}#${r.vehicleModelId}`));

  const gaps: RoutePriceGap[] = [];
  for (const m of activeModels) {
    const regions = regionsFor(m);
    for (const [k, rep] of serviced) {
      if (!regions.includes(rep.region)) continue;
      if (covered.has(`${k}#${m.id}`)) continue;
      gaps.push({
        region: rep.region,
        originType: rep.originType,
        originCode: rep.originCode,
        destType: rep.destType,
        destCode: rep.destCode,
        vehicleModelId: m.id,
        modelName: m.name,
      });
    }
  }

  return gaps.sort(
    (a, b) =>
      a.region.localeCompare(b.region) ||
      a.modelName.localeCompare(b.modelName) ||
      a.originCode.localeCompare(b.originCode) ||
      a.destCode.localeCompare(b.destCode),
  );
};
